import type {
  AppState,
  GameEvent,
  Player,
  Sanction,
  SanctionType,
  SessionView,
  Trigger,
} from "../shared/types";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  csrfToken?: string;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body !== undefined) {
    headers.set("content-type", "application/json");
  }
  if (options.csrfToken) {
    headers.set("x-csrf-token", options.csrfToken);
  }

  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      ...options,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      credentials: "same-origin",
      headers,
    });
  } catch {
    throw new ApiError(
      "Le serveur est injoignable. Vérifie ta connexion puis réessaie.",
      0,
      "NETWORK_ERROR",
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const isJson = response.headers.get("content-type")?.includes("application/json");
  const payload = isJson ? ((await response.json()) as unknown) : null;

  if (!response.ok) {
    const error = payload as
      | { error?: string; message?: string; details?: unknown }
      | null;
    throw new ApiError(
      error?.message ?? "Une erreur inattendue est survenue.",
      response.status,
      error?.error ?? "UNKNOWN_ERROR",
      error?.details,
    );
  }

  return payload as T;
}

export type PlayerInput = Pick<Player, "name" | "emoji" | "color" | "active">;
export type SanctionTypeInput = Pick<SanctionType, "name" | "emoji" | "color">;
export type SanctionInput = Pick<Sanction, "typeId" | "label" | "description">;
export type TriggerInput = Pick<
  Trigger,
  "label" | "speaker" | "sanctionId" | "active" | "sortOrder"
>;

export interface HistoryPage {
  items: GameEvent[];
  nextBefore: string | null;
}

export const api = {
  login: (password: string) =>
    request<SessionView>("/auth/login", {
      method: "POST",
      body: { password },
    }),

  logout: (csrfToken: string) =>
    request<void>("/auth/logout", { method: "POST", csrfToken }),

  session: () => request<SessionView>("/session"),

  choosePlayer: (csrfToken: string, playerId: string, takeover = false) =>
    request<SessionView>("/session/player", {
      method: "PUT",
      body: { playerId, takeover },
      csrfToken,
    }),

  state: () => request<AppState>("/state"),

  history: (before?: string, limit = 50) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (before) params.set("before", before);
    return request<HistoryPage>(`/history?${params.toString()}`);
  },

  trigger: (csrfToken: string, triggerId: string, idempotencyKey: string) =>
    request<AppState>("/events", {
      method: "POST",
      body: { triggerId, idempotencyKey },
      csrfToken,
    }),

  undo: (csrfToken: string, eventId: string) =>
    request<AppState>(`/events/${encodeURIComponent(eventId)}/undo`, {
      method: "POST",
      csrfToken,
    }),

  newGame: (csrfToken: string, idempotencyKey: string) =>
    request<AppState>("/games", {
      method: "POST",
      body: { idempotencyKey },
      csrfToken,
    }),

  createPlayer: (csrfToken: string, input: PlayerInput) =>
    request<Player>("/players", { method: "POST", body: input, csrfToken }),
  updatePlayer: (csrfToken: string, player: Player, input: PlayerInput) =>
    request<Player>(`/players/${encodeURIComponent(player.id)}`, {
      method: "PUT",
      body: { ...input, version: player.version },
      csrfToken,
    }),
  archivePlayer: (csrfToken: string, player: Player) =>
    request<void>(
      `/players/${encodeURIComponent(player.id)}?version=${player.version}`,
      { method: "DELETE", csrfToken },
    ),

  createSanctionType: (csrfToken: string, input: SanctionTypeInput) =>
    request<SanctionType>("/sanction-types", {
      method: "POST",
      body: input,
      csrfToken,
    }),
  updateSanctionType: (
    csrfToken: string,
    sanctionType: SanctionType,
    input: SanctionTypeInput,
  ) =>
    request<SanctionType>(
      `/sanction-types/${encodeURIComponent(sanctionType.id)}`,
      {
        method: "PUT",
        body: { ...input, version: sanctionType.version },
        csrfToken,
      },
    ),
  archiveSanctionType: (csrfToken: string, sanctionType: SanctionType) =>
    request<void>(
      `/sanction-types/${encodeURIComponent(sanctionType.id)}?version=${sanctionType.version}`,
      { method: "DELETE", csrfToken },
    ),

  createSanction: (csrfToken: string, input: SanctionInput) =>
    request<Sanction>("/sanctions", { method: "POST", body: input, csrfToken }),
  updateSanction: (csrfToken: string, sanction: Sanction, input: SanctionInput) =>
    request<Sanction>(`/sanctions/${encodeURIComponent(sanction.id)}`, {
      method: "PUT",
      body: { ...input, version: sanction.version },
      csrfToken,
    }),
  archiveSanction: (csrfToken: string, sanction: Sanction) =>
    request<void>(
      `/sanctions/${encodeURIComponent(sanction.id)}?version=${sanction.version}`,
      { method: "DELETE", csrfToken },
    ),

  createTrigger: (csrfToken: string, input: TriggerInput) =>
    request<Trigger>("/triggers", { method: "POST", body: input, csrfToken }),
  updateTrigger: (csrfToken: string, trigger: Trigger, input: TriggerInput) =>
    request<Trigger>(`/triggers/${encodeURIComponent(trigger.id)}`, {
      method: "PUT",
      body: { ...input, version: trigger.version },
      csrfToken,
    }),
  archiveTrigger: (csrfToken: string, trigger: Trigger) =>
    request<void>(
      `/triggers/${encodeURIComponent(trigger.id)}?version=${trigger.version}`,
      { method: "DELETE", csrfToken },
    ),
};

export function friendlyError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "VERSION_CONFLICT") {
      return "Quelqu’un a modifié cet élément entre-temps. Les données ont été actualisées.";
    }
    if (error.status === 429) {
      return "Trop d’essais. Attends quelques minutes avant de recommencer.";
    }
    return error.message;
  }
  return "Une erreur inattendue est survenue.";
}
